import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject, take } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CONFIG } from './core/config/config.token';
import { HeaderComponent } from './shared/composants/header/header.component';
import { LoaderComponent } from './shared/composants/loader/loader.component';
import { SidenavComponent } from './shared/composants/sidenav/sidenav.component';
import { LoaderService } from './shared/services/loader.service';
import { SidenavService } from './shared/services/sidenav.service';
import { RefDataService } from './shared/ref.data.service';
import { AuthOrchestratorService } from './core/auth/auth-orchestrator.service';
import { AuthService } from './core/auth/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, SidenavComponent, RouterOutlet, LoaderComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly config = inject(CONFIG);
  private readonly router = inject(Router);

  private readonly authOrchestrator = inject(AuthOrchestratorService);
  private readonly refDataService = inject(RefDataService);
  private readonly authService = inject(AuthService);
  private readonly sidenavService = inject(SidenavService);
  private readonly loaderService = inject(LoaderService);

  isAuthenticated: boolean = false;
  userInfo: any;
  showSidenav: boolean = true;
  isAuthSuccess: boolean = false;
  private readonly unsubscribe$ = new Subject<void>(); // Manages subscriptions to avoid memory leaks.
  public loaderIsVisible = false;
  public loaderMessage = '';

  async ngOnInit() {
    // Favicon
    this.setFavicon();

    // Sidenav visibility
    this.sidenavService.isSidenavOpen$.pipe(take(1)).subscribe((isOpen) => {
      this.showSidenav = isOpen;
    });

    // Subscribes to router events and updates the showSidenav property based on the current route.
    await this.handleAuthCallback();

    // load user info + parameter points
    const isAuthenticated = await this.authService.isPrincipalAuthenticated();
    if (isAuthenticated) {
      this.refDataService.loadParametersPoints();
      this.checkSidenavVisibility();
      await this.authService.loadUserInfo();
    }

    this.router.events
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$), // Ensures that subscription is automatically unsubscribed when the component is destroyed.
      )
      .subscribe((event: NavigationEnd) => {
        this.checkSidenavVisibility();
      });

    // Subscribes to sidenav service to update the sidenav width dynamically.
    this.sidenavService.isSidenavOpen$
      .pipe(
        takeUntil(this.unsubscribe$), // Ensures that subscription is automatically unsubscribed when the component is destroyed.
      )
      .subscribe((isOpen) => {
        this.updateWidthSideNav(isOpen);
      });

    // Subscribes to userInfo changes from AuthenticatedService.
    this.authService.userInfo$
      .pipe(
        takeUntil(this.unsubscribe$), // Ensures that subscription is automatically unsubscribed when the component is destroyed.
      )
      .subscribe((userInfo: any) => {
        this.userInfo = userInfo;
      });

    // Subscribe to loader service
    this.loaderService.loaderIsVisible
      .pipe(
        takeUntil(this.unsubscribe$), // Ensures that subscription is automatically unsubscribed when the component is destroyed.
      )
      .subscribe((loaderIsVisible: boolean) => {
        this.loaderIsVisible = loaderIsVisible;
      });

    this.loaderService.message
      .pipe(
        takeUntil(this.unsubscribe$), // Ensures that subscription is automatically unsubscribed when the component is destroyed.
      )
      .subscribe((loaderMessage) => {
        this.loaderMessage = loaderMessage;
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(); // Triggers the completion of all observables.
    this.unsubscribe$.complete(); // Ensures no further subscriptions are taken after the component is destroyed.
  }

  private setFavicon() {
    const link: HTMLLinkElement | null = document.getElementById('favicon') as HTMLLinkElement;
    if (link) {
      link.href = 'assets/' + this.config.favicon;
    }
  }

  private updateWidthSideNav(isOpen: boolean) {
    // Directly manipulates the DOM to adjust the width of the sidenav and the margin of the main content.
    const sidenav = document.querySelector('app-sidenav') as HTMLElement;

    if (!sidenav) {
      return;
    }

    if (isOpen && !sidenav.classList.contains('open')) {
      sidenav.classList.add('open');
    } else if (!isOpen && sidenav.classList.contains('open')) {
      sidenav.classList.remove('open');
    }
  }

  checkSidenavVisibility() {
    const currentPath = globalThis.location.pathname;
    const noSidenavRoutes = ['/access-error'];

    this.authService.isPrincipalAuthenticated().then((isAuthenticated) => {
      this.showSidenav = !noSidenavRoutes.includes(currentPath) && isAuthenticated;
    });
  }

  async handleAuthCallback(): Promise<void> {
    const pathName = globalThis.location.pathname;
    if (!pathName.startsWith('/callback')) {
      return;
    }
    const urlParams = new URLSearchParams(globalThis.location.search);
    const state = urlParams.get('state') ?? '';
    await this.authOrchestrator.handleCallback(state);
  }
}
